import type { DomainMutationOptions } from '@/domain/shared/types';
import { useSmoltReportController_createReport } from '@/services/hooks';
import type {
    CreateSmoltReportDto,
    SmoltReportController_createReportQueryParams,
    SmoltReportCreateResponseDto,
} from '@/services/types';

import { mapCreateSmoltReportRequestToApi, mapSmoltReportCreateResponseToDomain } from './mappers';
import type { CreateSmoltReportVariables, UsePostCreateReportResult } from './types';

type UsePostCreateReportOptions = DomainMutationOptions<
    {
        cageId: number;
        requestBody: CreateSmoltReportDto;
        queryParams?: SmoltReportController_createReportQueryParams;
    },
    SmoltReportCreateResponseDto
>;

/**
 * Domain hook to create a new smolt report for a cage.
 *
 * @param options - Optional mutation options (e.g. onSuccess, onError)
 * @returns Mutation result with mutateAsync returning mapped created report
 */
export const usePostCreateReport = (
    options?: UsePostCreateReportOptions
): UsePostCreateReportResult => {
    const mutation = useSmoltReportController_createReport(options);

    const toApiVariables = (variables: CreateSmoltReportVariables) => {
        const { cageId, requestBody, fishGroupId } = variables;
        return {
            cageId,
            requestBody: mapCreateSmoltReportRequestToApi(requestBody),
            // Only with a group, so a cage report is the exact request it always was.
            ...(fishGroupId != null && { queryParams: { fishGroupId } }),
        };
    };

    const mutate = (variables: CreateSmoltReportVariables) => {
        mutation.mutate(toApiVariables(variables));
    };

    const mutateAsync = async (variables: CreateSmoltReportVariables) => {
        const result = await mutation.mutateAsync(toApiVariables(variables));
        return mapSmoltReportCreateResponseToDomain(result.data);
    };

    return {
        ...mutation,
        isLoading: mutation.isPending,
        mutate,
        mutateAsync,
        data:
            mutation.data?.data != null
                ? mapSmoltReportCreateResponseToDomain(mutation.data.data)
                : undefined,
    };
};
